import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  select,
} from '@ngrx/store';
import { pipe } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { AllActionsUnion } from '../../../app.actions';
import { Reservation } from '../../../models/reservation/reservation';
import {
  resetState,
  storeErrorMessage,
  storeReservations,
  updateLoadingState,
} from './reservations.actions';

export type LoadingState = 'none' | 'loading' | 'loaded';

export interface ReservationsState {
  loadingState: LoadingState;
  errorMessage: string;
  reservations: Reservation[];
}

const initialState: ReservationsState = {
  loadingState: 'none',
  errorMessage: '',
  reservations: [],
};

export const featureName = 'reservationsFeature';

export function reservationsReducer(
  state_: ReservationsState | undefined,
  action: AllActionsUnion,
): ReservationsState {
  const reducer_ = createReducer<ReservationsState>(
    initialState,
    on(
      updateLoadingState,
      (state, { payload }): ReservationsState => {
        return { ...state, loadingState: payload };
      },
    ),
    on(
      storeErrorMessage,
      (state, { payload }): ReservationsState => {
        return { ...state, errorMessage: payload };
      },
    ),
    on(
      storeReservations,
      (state, { payload }): ReservationsState => {
        return { ...state, reservations: payload };
      },
    ),
    on(
      resetState,
      (): ReservationsState => {
        return { ...initialState };
      },
    ),
  );
  return reducer_(state_, action);
}

const getFeature = createFeatureSelector<ReservationsState>(featureName);

export const getLoadingState = pipe(
  select(
    createSelector(
      getFeature,
      v => v.loadingState,
    ),
  ),
  distinctUntilChanged(),
);

export const getErrorMessage = pipe(
  select(
    createSelector(
      getFeature,
      v => v.errorMessage,
    ),
  ),
  filter(message => message !== ''),
  distinctUntilChanged(),
);

export const getReservations = pipe(
  select(
    createSelector(
      getFeature,
      v => v.reservations,
    ),
  ),
  distinctUntilChanged((a, b) => {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((v, i) => v.eq(b[i]));
  }),
);
